/**
 * User-facing job handle: identity plus a progress/result view.
 * Port of fastSDK's `APISeex`: a ticket, not an orchestrator. Lifecycle
 * actions (cancel, stream, result) delegate to the per-job `JobRuntime`.
 *
 * A Job is thenable: `await job` resolves to the final (media-parsed) result,
 * assembling the full stream payload when the job streams and `stream()` was
 * never consumed.
 */

import { EndpointDefinition } from '../schemas/definitions';
import {
  JobStatus,
  SocaityJobResponse,
  isTerminalStatus,
  jobStatusFromString,
} from '../schemas/transport';
import type { JobRuntime } from './runtime';
import type { StreamSession } from '../streaming/session';

export type JobEvent = 'status' | 'progress' | 'completed' | 'failed';

type Listener = (payload: any) => void;

export class Job<T = any> implements PromiseLike<T> {
  /** Latest job envelope from the API, or null before the first response. */
  response: SocaityJobResponse | null = null;

  private currentStatus: JobStatus = JobStatus.PENDING;
  private listeners = new Map<JobEvent, Set<Listener>>();
  private completedPayload: { result?: T; error?: Error } | null = null;

  /** @internal assigned by the client at submit time. */
  runtime!: JobRuntime<T>;

  constructor(public readonly endpoint: EndpointDefinition) {}

  // ------------------------------------------------------------------
  // State view
  // ------------------------------------------------------------------

  /** Remote job id, once the service assigned one. */
  get id(): string | undefined {
    return this.response?.job_id;
  }

  /** Unified job status (PENDING → QUEUED/PROCESSING/STREAMING → terminal). */
  get status(): JobStatus {
    return this.currentStatus;
  }

  /** Remote progress in [0, 1], or null when unknown. */
  get progress(): number | null {
    return this.response?.progress ?? null;
  }

  get message(): string | null {
    return this.response?.message ?? null;
  }

  get isTerminal(): boolean {
    return isTerminalStatus(this.currentStatus);
  }

  // ------------------------------------------------------------------
  // Lifecycle delegation
  // ------------------------------------------------------------------

  /**
   * Open the job's live output stream. Resolves once a stream source exists
   * (a direct SSE response or the job's `links.stream`). One session per job.
   */
  stream(options?: { timeoutMs?: number }): Promise<StreamSession> {
    return this.runtime.stream(options);
  }

  /**
   * Wait for and return the final result. Media payloads arrive as typed
   * media-toolkit files; streamed output is assembled into text or media.
   */
  getResult(): Promise<T> {
    return this.runtime.getResult();
  }

  /** Cancel locally and remotely (when the service exposes a cancel link). */
  cancel(): Promise<boolean> {
    return this.runtime.cancel();
  }

  then<R1 = T, R2 = never>(
    onfulfilled?: ((value: T) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: any) => R2 | PromiseLike<R2>) | null,
  ): Promise<R1 | R2> {
    return this.getResult().then(onfulfilled, onrejected);
  }

  catch<R = never>(onrejected?: ((reason: any) => R | PromiseLike<R>) | null): Promise<T | R> {
    return this.getResult().catch(onrejected);
  }

  finally(onfinally?: (() => void) | null): Promise<T> {
    return this.getResult().finally(onfinally);
  }

  // ------------------------------------------------------------------
  // Events
  // ------------------------------------------------------------------

  on(event: JobEvent, listener: Listener): this {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(listener);
    // Replay terminal events for late subscribers.
    if (this.completedPayload) {
      if (event === 'completed' && this.completedPayload.error === undefined) listener(this.completedPayload.result);
      if (event === 'failed' && this.completedPayload.error !== undefined) listener(this.completedPayload.error);
    }
    return this;
  }

  onStatus(listener: (status: JobStatus) => void): this {
    return this.on('status', listener);
  }

  onProgress(listener: (update: { progress: number | null; message: string | null }) => void): this {
    return this.on('progress', listener);
  }

  onCompleted(listener: (result: T) => void): this {
    return this.on('completed', listener);
  }

  onFailed(listener: (error: Error) => void): this {
    return this.on('failed', listener);
  }

  private emit(event: JobEvent, payload: any): void {
    this.listeners.get(event)?.forEach((listener) => listener(payload));
  }

  // ------------------------------------------------------------------
  // Internal state transitions (driven by the runtime)
  // ------------------------------------------------------------------

  /** @internal */
  _setStatus(status: JobStatus): void {
    if (status === this.currentStatus) return;
    this.currentStatus = status;
    this.emit('status', status);
  }

  /** @internal */
  _applyResponse(response: SocaityJobResponse): void {
    this.response = response;
    this._setStatus(jobStatusFromString(response.status));
    this.emit('progress', { progress: this.progress, message: this.message });
  }

  /** @internal */
  _finish(result: T): void {
    if (this.completedPayload) return;
    this.completedPayload = { result };
    this._setStatus(JobStatus.FINISHED);
    this.emit('completed', result);
  }

  /** @internal */
  _fail(error: Error, status: JobStatus = JobStatus.FAILED): void {
    if (this.completedPayload) return;
    this.completedPayload = { error };
    this._setStatus(status);
    this.emit('failed', error);
  }
}
